import type Step from "src/pages/GetStarted/Step";
import type {
  QualifyingQuestions,
  UserSetupQuestions,
} from "src/interfaces/onboarding.interface";

export interface SystemState {
  loggedIn: boolean;
  user?: User;
  loading: boolean;
  error: any;
  authorizing: boolean;
  passwordResetSubmitted: boolean;
  submitting: boolean;
  submitted: boolean;
  ebayAuthUrl?: string;
  ebayAuthLoading?: boolean;
  ebayAuth: EbayAuth | null;
  ebayIntegration: boolean;
  signupSuccess: boolean;
  claims: Record<string, any>;
  /** True when dashboard date range was set optimistically (custom only); skip clamping until next load. */
  dashboardDateRangeOptimistic?: boolean;
  /** True when transaction date range was set optimistically (custom only); skip clamping until next load. */
  transactionDateRangeOptimistic?: boolean;
}

export interface EbayAuth {
  access_token: string;
  expiration_time: number | string;
  refresh_token: string;
  refresh_token_expiration_time: number | string;
  token_type: string;
}

export type DontShowDialog =
  | "maintenance_activities_dialog"
  | "walk_through_notifications_icon"
  | "walk_through_notifications_review_new_sales"
  | "walk_through_notifications_review_pending_inventory"
  | "walk_through_notifications_review_new_expenses"
  | "walk_through_notifications_review_new_funds"
  | "walk_through_review_sales"
  | "walk_through_auto_matching_sales"
  | "walk_through_manual_matching_sales"
  | "walk_through_duplicate_inventory_checker"
  | "walk_through_inventory_import"
  | "walk_through_pending_inventory_checker"
  | "walk_through_pending_inventory_import"
  | "walk_through_categorize_expenses"
  | "walk_through_review_refunds";

export const FEATURE_FLAGS = [
  "amazon",
  "accountant",
  "pendingInventory",
  "deferUnreviewedLoad",
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

export type ExpenseType =
  | "advertising"
  | "car-and-truck-expenses"
  | "comissions-and-fees"
  | "contract-labor"
  | "depletion"
  | "depreciation-and-section-179-expense-deduction"
  | "employee-benefit-programs"
  | "insurance"
  | "interest-mortage"
  | "interest-other"
  | "legal-and-professional-services"
  | "office-expense"
  | "pension-and-profit-sharing-plans"
  | "rent-vehicles-machinery-and-equipment"
  | "rent-other-business-property"
  | "repairs-and-maintenance"
  | "supplies"
  | "taxes-and-licenses"
  | "travel-and-meals-travel"
  | "travel-and-meals-deductible-meals"
  | "utilities"
  | "wages"
  | "other-expenses"
  | "energy-efficient-commercial-bldgs-deduction"
  | "purchases-less-cost-of-items-withdrawn-for-personal-use";

/**
 * Custom range: `yyyy-mm-dd` strings in the user's stored timezone (new writes),
 * or legacy `Date` / Firestore `Timestamp` (normalized in the reducer).
 */
export type StoredCustomDateRange = {
  start: string | Date;
  end: string | Date;
};

/** Stored date range: preset key or custom { start, end }. */
export type StoredDateRange =
  "day" | "week" | "month" | "year" | StoredCustomDateRange;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  planConfirm?: boolean;
  income_tax_rate?: number;
  pm_id?: string;
  uid?: string;
  customer_id?: string;
  plan?: string;
  subscriptionRunning?: boolean;
  subscriptionResponse?: { success: boolean; message: string } | null;
  noError?: boolean;
  updateNO?: boolean;
  acceptedTOS: boolean;
  checklist_completed?: boolean; // XXX: deprecated, used in old "User Checklist" (remove `checklist_items: {}`)
  ebayError?: boolean;
  expense_rules?: string[];
  tables_configuration?: Record<string, string>;
  analytics_groupings?: Record<string, Record<string, string[]>>;
  allowed_ebay_integrations?: number;
  salesQuickView?: boolean;
  getStartedStep?: Step;
  inventoryType?: "accrual" | "cash";
  dontShowDialogs?: DontShowDialog[];
  featureFlags?: Partial<Record<FeatureFlag, boolean>>;
  scheduleCMappings?: Record<string, ExpenseType>; // <account name> = Expense Type
  scheduleCGroupings?: Record<string, string>; // <account name> = <grouping name>
  trialEndDate?: { seconds: number };
  company?: {
    name: string;
    logo?: string;
  };

  qualifyingQuestions?: QualifyingQuestions;
  setupQuestions?: UserSetupQuestions;
  createdAt?: { toDate(): Date };
  dashboardDateRange?: StoredDateRange;
  transactionDateRange?: StoredDateRange;
  themePreferences?: ThemePreferences;
  /** IANA timezone id from the client (e.g. `America/New_York`), set once when missing on the user doc. */
  timezone?: string;
}

export interface TableConfiguration {
  hide?: Record<string, boolean>;
  order?: Record<string, number>;
  width?: Record<string, number>;
}

export interface AnalyticsGroupingItem {
  department: string;
  category: string;
  subcategory: string;
}

export interface ThemePreferences {
  mode: "light" | "dark";
  useCustomPalette: boolean;
  customLightPalette: {
    bg: string;
    card: string;
    text: string;
    border: string;
    accent: string;
    brandPrimary: string;
    brandSecondary: string;
  };
  customDarkPalette: {
    bg: string;
    card: string;
    text: string;
    border: string;
    accent: string;
    brandPrimary: string;
    brandSecondary: string;
  };
}
